export type LoginOptions = {
  preferredApp?: string
  candidateNext?: string | null
  candidateOnboarding?: boolean
}

const CANDIDATE_VACANCY_DESTINATION = /^\/postulate\/candidate\/vacancies\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function resolveCandidateNext(value: string | null | undefined): string | null {
  if (!value || !CANDIDATE_VACANCY_DESTINATION.test(value)) return null
  return value
}

export type AccessOverrideInput = {
  permission_key: string
  effect: 'allow' | 'deny'
  reason: string
  expires_at: string | null
}

export type AccessAssignment = {
  id: string
  organization_id: string
  user_id: string
  application_id: string
  role_id: string
  expires_at: string | null
  revoked_at: string | null
  version: number
  access_applications?: { key: string; display_name: string } | null
  access_roles?: { key: string; display_name: string } | null
  user_permission_overrides?: Array<{
    id: string
    effect: 'allow' | 'deny'
    reason: string
    expires_at: string | null
    access_permissions?: { key: string; display_name?: string } | null
  }>
}

export type AccessAssignmentInput = {
  organization_id: string
  user_id: string
  app_key: string
  role_id: string
  expires_at?: string | null
}

export type AccessAssignmentSyncInput = AccessAssignmentInput & {
  expected_version: number
  overrides: AccessOverrideInput[]
}

export type AccessAssignmentDraft = Pick<AccessAssignmentSyncInput, 'role_id' | 'overrides'> & {
  expires_at?: string | null
}

export type AssignmentState = {
  organizationId: string | null
  assignments: AccessAssignment[]
  draft: AccessAssignmentDraft | null
  conflict: boolean
}

export const initialAssignmentState: AssignmentState = {
  organizationId: null,
  assignments: [],
  draft: null,
  conflict: false,
}

export type AssignmentStateAction =
  | { type: 'organization-selected'; organizationId: string | null }
  | { type: 'loaded'; organizationId: string; assignments: AccessAssignment[] }
  | { type: 'draft-changed'; draft: AccessAssignmentDraft }
  | { type: 'sync-conflict' }
  | { type: 'sync-complete'; assignment: AccessAssignment }

export function assignmentStateReducer(
  state: AssignmentState,
  action: AssignmentStateAction,
): AssignmentState {
  if (action.type === 'organization-selected') {
    return {
      organizationId: action.organizationId,
      assignments: [],
      draft: null,
      conflict: false,
    }
  }
  if (action.type === 'loaded') {
    if (state.organizationId && state.organizationId !== action.organizationId) return state
    return { ...state, organizationId: action.organizationId, assignments: action.assignments }
  }
  if (action.type === 'draft-changed') return { ...state, draft: action.draft, conflict: false }
  if (action.type === 'sync-conflict') return { ...state, conflict: true }
  return {
    ...state,
    assignments: state.assignments.map((candidate) =>
      candidate.id === action.assignment.id ? action.assignment : candidate,
    ),
    draft: null,
    conflict: false,
  }
}

export function activeAssignments(assignments: AccessAssignment[], now = new Date()): AccessAssignment[] {
  return assignments.filter((assignment) => {
    if (assignment.revoked_at) return false
    if (!assignment.expires_at) return true
    const expiresAt = Date.parse(assignment.expires_at)
    return Number.isFinite(expiresAt) && expiresAt > now.getTime()
  })
}

export type LoginProfile = {
  role?: string | null
  user_type?: string | null
}

export type LoginAppAssignment = {
  app: string
  is_active: boolean
  expires_at?: string | null
}

export type OrganizationAppAccess = LoginAppAssignment & {
  organization_id: string
  organization_name: string | null
}

export function hasActiveOrganizationAppAccess(
  assignments: OrganizationAppAccess[],
  organizationId: string | null,
  app: string,
  now = new Date(),
): boolean {
  if (!organizationId) return false
  return assignments.some((assignment) =>
    assignment.organization_id === organizationId &&
    assignment.app === app &&
    isAssignmentActive(assignment, now),
  )
}

export type LoginDestinationInput = LoginOptions & {
  profile: LoginProfile | null | undefined
  platformRole?: 'owner' | 'super_admin' | null
  assignments: LoginAppAssignment[]
  now?: Date
}

function isPlatformAdmin(platformRole?: 'owner' | 'super_admin' | null): boolean {
  return platformRole === 'owner' || platformRole === 'super_admin'
}

function isAssignmentActive(assignment: LoginAppAssignment, now: Date): boolean {
  if (!assignment.is_active) return false
  if (!assignment.expires_at) return true

  const expiresAt = Date.parse(assignment.expires_at)
  return Number.isFinite(expiresAt) && expiresAt > now.getTime()
}

function appAdminDestination(app: string): string {
  if (app === 'postulate') return '/postulate/admin/dashboard'
  if (app === 'questionnaire') return '/questionnaire/admin'
  if (!/^[a-z0-9_-]+$/.test(app)) return '/'
  return `/${app}/admin/dashboard`
}

export function resolveLoginDestination({
  profile,
  platformRole,
  assignments,
  preferredApp,
  candidateNext,
  candidateOnboarding,
  now = new Date(),
}: LoginDestinationInput): string {
  if (isPlatformAdmin(platformRole)) {
    if (preferredApp === 'questionnaire') {
      return '/questionnaire/super-admin/clients'
    }
    return '/admin/dashboard'
  }

  if (profile?.user_type === 'candidate') {
    if (candidateOnboarding) {
      const next = resolveCandidateNext(candidateNext)
      return `/postulate/candidate/profile/complete_profile${next ? `?next=${encodeURIComponent(next)}` : ''}`
    }
    return resolveCandidateNext(candidateNext) || '/postulate/candidate/dashboard'
  }

  const activeAssignments = assignments.filter((assignment) =>
    isAssignmentActive(assignment, now),
  )

  if (preferredApp) {
    const canAccessPreferredApp = activeAssignments.some(
      (assignment) => assignment.app === preferredApp,
    )
    return canAccessPreferredApp ? appAdminDestination(preferredApp) : '/'
  }

  const firstConcreteAssignment = activeAssignments.find((assignment) => assignment.app !== '*')
  return firstConcreteAssignment
    ? appAdminDestination(firstConcreteAssignment.app)
    : '/'
}


export function resolveExpiredSessionLogin(pathname: string, search = ''): string {
  if (pathname.startsWith('/postulate/candidate/') || pathname.startsWith('/candidate/')) {
    const next = resolveCandidateNext(pathname) || resolveCandidateNext(new URLSearchParams(search).get('next'))
    return `/candidate/login?expired=true${next ? `&next=${encodeURIComponent(next)}` : ''}`
  }
  return '/admin/login?expired=true'
}
